// Third-party imports
import { createBrowserRouter, RouteObject } from 'react-router-dom';

// Constants and Enums
import { PAGE_TITLE } from '../constants/common';
import { Action } from '../enums/action';
import ROUTE from '../constants/route-constants';

// Layouts
import DefaultLayout from '../layout/DefaultLayout';

// Components
import PageTitle from '../components/PageTitle';
import PermissionRoute from '../components/PermissionRoute/PermissionRoute';
import EditOutlet from '../pages/Recipes/Components/EditOutlet';

// Authentication Pages
import ForgotPassword from '../pages/Authentication/ForgotPassword';
import SignIn from '../pages/Authentication/SignIn';

// General Pages
import ForbiddenResourceView from '../pages/ForbiddenResourceView/ForbiddenResourceView';
import NotFoundPage from '../pages/Pages/NotFoundPage';
import Profile from '../pages/Pages/Profile';

// Contracts
import ContractsView from '../pages/ContractsView/ContractsView';
import { ContractScreens as ContractScreensView } from '../pages/ContractScreensView';
import CreateContract from '../pages/CreateContract/CreateContract';
import EditContract from '../pages/EditContract/EditContract';

// Controllers
import ControllersView from '../pages/ControllersView/ControllersView';

// Cycles
import AddCycle from '../pages/AddCycle/AddCycle';
import CycleReport from '../components/ReportPages/CycleReport/CycleReport';
import CycleView from '../pages/CycleView/CycleView';
import EditCycle from '../pages/EditCycle/EditCycle';

// Locations
import CreateLocation from '../pages/CreateLocation/CreateLocation';
import EditLocation from '../pages/EditLocation/EditLocation';
import LocationContractsView from '../pages/LocationContractsView/LocationContractsView';
import LocationsView from '../pages/LocationsView/LocationsView';

// Members
import CreateMember from '../pages/CreateMember/CreateMember';
import EditMember from '../pages/EditMember/EditMember';
import MembersView from '../pages/MembersView/MembersView';

// Organizations
import OrganizationsView from '../pages/OrganizationsView/OrganizationsView';

// Screens
import CreateScreen from '../pages/CreateScreen/CreateScreen';
import ScreenCyclesView from '../pages/ScreenCyclesView/ScreenCyclesView';

// Recipes
import RecipeCreate from '../pages/Recipes/SubPages/RecipeCreate/RecipeCreate';
import RecipeStepCreate from '../pages/Recipes/SubPages/RecipeStepCreate/RecipeStepCreate';
import RecipeSteps from '../pages/Recipes/SubPages/RecipeSteps/RecipeSteps';
import RecipesView from '../pages/Recipes/SubPages/RecipesView/RecipesView';

// Edit Controller Pages
import CalibrationCertificateForm from '../pages/EditController/SubPages/CalibrationCertificates/CalibrationCertificateForm';
import CalibrationCertificates from '../pages/EditController/SubPages/CalibrationCertificates/CalibrationCertificates';
import Channels from '../pages/EditController/SubPages/Channels/Channels';
import ChannelFaults from '../pages/EditController/SubPages/ChannelFaults/ChannelFaults';
import EditChannelWrapper from '../pages/EditController/SubPages/EditChannel/EditChannelWrapper';
import EditControllerOutlet from '../pages/EditController/Components/EditControllerOutlet';
import FaultControl from '../pages/EditController/SubPages/FaultControl/FaultControl';
import Settings from '../pages/EditController/SubPages/Settings/Settings';
import RecipeStepEdit from '../pages/Recipes/SubPages/RecipeStepEdit/RecipeStepEdit';
import RecipeEdit from '../pages/Recipes/SubPages/RecipeEdit/RecipeEdit';
import { models } from '../store/models';
import { SystemFaults } from '../pages/EditController/SubPages/SystemFaults/SystemFaults';
import ChannelsNames from '../pages/EditController/SubPages/ChannelsNames';
import ChannelColors from '../pages/EditController/SubPages/ChannelColors';
import HomePageChecker from '../components/HomePageChecker/HomePageChecker';
import EditScreen from '../pages/EditScreen/EditScreen';
import ResetPassword from '../pages/Authentication/ResetPassword';
import { PowerShare } from '../pages/EditController/SubPages/PowerShare/PowerShare';
import EditController from '../pages/EditController/SubPages/Edit';
import LocationContractOverview from '../pages/LocationContractOverview/LocationContractOverview';
import PreHeatRecipeEdit from '../pages/PreHeatRecipes/SubPages/PreHeatRecipeEdit/PreHeatRecipeEdit';

type RoutePath = (typeof ROUTE)[keyof typeof ROUTE];
type CrumbItem = {
  name?: keyof typeof models;
  singularEntityName?: string;
  fieldName?: string;
  secondFieldName?: string
  pageName?: string;
  isStaticPage?: boolean;
  path: RoutePath;
};

export type Crumb = {
  text?: string;
  segments?: CrumbItem[];
  isSkip?: boolean;
};

export const HOME_PAGE_NAME = 'Dashboard';
export const EMPTY_CRUMB_TEXT = 'EMPTY_CRUMB_TEXT';

const pagesEditControl: RouteObject = {
  handle: {
    crumb: (): Crumb => ({ text: EMPTY_CRUMB_TEXT, isSkip: true }),
  },
  element: (
    <EditControllerOutlet />
  ),
  children: [
    {
      path: ROUTE.EDIT_CONTROLLER,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { pageName: PAGE_TITLE.CONTROLLERS, path: ROUTE.CONTROLLERS, isStaticPage: true },
            { name: 'controllers', singularEntityName: 'controller', fieldName: 'name', path: ROUTE.EDIT_CONTROLLER }
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.EDIT_CONTROLLER}>
          <PageTitle title={PAGE_TITLE.EDIT_CONTROLLER} />
          <EditController />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.CONTROLLER_CHANNELS,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { pageName: PAGE_TITLE.CONTROLLERS, path: ROUTE.CONTROLLERS, isStaticPage: true },
            { name: 'controllers', singularEntityName: 'controller', fieldName: 'name', path: ROUTE.EDIT_CONTROLLER },
            { pageName: PAGE_TITLE.CONTROLLER_CHANNELS, path: ROUTE.CONTROLLER_CHANNELS, isStaticPage: true }
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.CONTROLLER_CHANNELS}>
          <PageTitle title={PAGE_TITLE.CONTROLLER_CHANNELS} />
          <Channels />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.CONTROLLER_CHANNEL_FAULTS,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { pageName: PAGE_TITLE.CONTROLLERS, path: ROUTE.CONTROLLERS, isStaticPage: true },
            { name: 'controllers', singularEntityName: 'controller', fieldName: 'name', path: ROUTE.EDIT_CONTROLLER },
            { pageName: PAGE_TITLE.CONTROLLER_CHANNEL_FAULTS, path: ROUTE.CONTROLLER_CHANNEL_FAULTS, isStaticPage: true }
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.CONTROLLER_CHANNEL_FAULTS}>
          <PageTitle title={PAGE_TITLE.CONTROLLER_CHANNEL_FAULTS} />
          <ChannelFaults />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.CONTROLLER_CHANNELS_NAMES,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { pageName: PAGE_TITLE.CONTROLLERS, path: ROUTE.CONTROLLERS, isStaticPage: true },
            { name: 'controllers', singularEntityName: 'controller', fieldName: 'name', path: ROUTE.EDIT_CONTROLLER },
            { pageName: PAGE_TITLE.CONTROLLER_CHANNELS_NAMES, path: ROUTE.CONTROLLER_CHANNELS_NAMES, isStaticPage: true }
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Update} page={PAGE_TITLE.CONTROLLER_CHANNELS}>
          <PageTitle title={PAGE_TITLE.EDIT_CHANNELS_NAMES} />
          <ChannelsNames />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.CONTROLLER_CHANNELS_COLORS,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { pageName: PAGE_TITLE.CONTROLLERS, path: ROUTE.CONTROLLERS, isStaticPage: true },
            { name: 'controllers', singularEntityName: 'controller', fieldName: 'name', path: ROUTE.EDIT_CONTROLLER },
            { pageName: PAGE_TITLE.CONTROLLER_CHANNELS_COLORS, path: ROUTE.CONTROLLER_CHANNELS_COLORS, isStaticPage: true }
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Update} page={PAGE_TITLE.CONTROLLER_CHANNELS}>
          <PageTitle title={PAGE_TITLE.EDIT_CHANNELS_COLORS} />
          <ChannelColors />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.EDIT_CONTROLLER_CHANNEL,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { pageName: PAGE_TITLE.CONTROLLERS, path: ROUTE.CONTROLLERS, isStaticPage: true },
            { name: 'controllers', singularEntityName: 'controller', fieldName: 'name', path: ROUTE.EDIT_CONTROLLER },
            { pageName: 'Channels', path: ROUTE.CONTROLLER_CHANNELS, isStaticPage: true },
            { name: 'channels', singularEntityName: 'channel', fieldName: 'name', secondFieldName: 'channel', path: ROUTE.EDIT_CONTROLLER_CHANNEL },
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.EDIT_CONTROLLER_CHANNEL}>
          <PageTitle title={PAGE_TITLE.EDIT_CONTROLLER_CHANNEL} />
          <EditChannelWrapper />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.EDIT_CONTROLLER_SETTINGS,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { pageName: PAGE_TITLE.CONTROLLERS, path: ROUTE.CONTROLLERS, isStaticPage: true },
            { name: 'controllers', singularEntityName: 'controller', fieldName: 'name', path: ROUTE.EDIT_CONTROLLER },
            { pageName: PAGE_TITLE.EDIT_CONTROLLER_SETTINGS, path: ROUTE.EDIT_CONTROLLER_SETTINGS, isStaticPage: true },
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.EDIT_CONTROLLER_SETTINGS}>
          <PageTitle title={PAGE_TITLE.EDIT_CONTROLLER_SETTINGS} />
          <Settings />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.EDIT_CONTROLLER_SYSTEM_FAULTS,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { pageName: PAGE_TITLE.CONTROLLERS, path: ROUTE.CONTROLLERS, isStaticPage: true },
            { name: 'controllers', singularEntityName: 'controller', fieldName: 'name', path: ROUTE.EDIT_CONTROLLER },
            { pageName: PAGE_TITLE.EDIT_CONTROLLER_SYSTEM_FAULTS, path: ROUTE.EDIT_CONTROLLER_SYSTEM_FAULTS, isStaticPage: true },
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.EDIT_CONTROLLER_SYSTEM_FAULTS}>
          <PageTitle title={PAGE_TITLE.EDIT_CONTROLLER_SYSTEM_FAULTS} />
          <SystemFaults />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.EDIT_CONTROLLER_FAULT_CONTROL,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { pageName: PAGE_TITLE.CONTROLLERS, path: ROUTE.CONTROLLERS, isStaticPage: true },
            { name: 'controllers', singularEntityName: 'controller', fieldName: 'name', path: ROUTE.EDIT_CONTROLLER },
            { pageName: PAGE_TITLE.EDIT_CONTROLLER_FAULT_CONTROL, path: ROUTE.EDIT_CONTROLLER_FAULT_CONTROL, isStaticPage: true },
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.EDIT_CONTROLLER_FAULT_CONTROL}>
          <PageTitle title={PAGE_TITLE.EDIT_CONTROLLER_FAULT_CONTROL} />
          <FaultControl />
        </PermissionRoute>
      ),
    },


    {
      path: ROUTE.EDIT_CONTROLLER_POWER_SHARE,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { pageName: PAGE_TITLE.CONTROLLERS, path: ROUTE.CONTROLLERS, isStaticPage: true },
            { name: 'controllers', singularEntityName: 'controller', fieldName: 'name', path: ROUTE.EDIT_CONTROLLER },
            { pageName: PAGE_TITLE.EDIT_CONTROLLER_POWER_SHARE, path: ROUTE.EDIT_CONTROLLER_POWER_SHARE, isStaticPage: true },
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.EDIT_CONTROLLER_POWER_SHARE}>
          <PageTitle title={PAGE_TITLE.EDIT_CONTROLLER_POWER_SHARE} />
          <PowerShare />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.CALIBRATION_CERTIFICATES,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { pageName: PAGE_TITLE.CONTROLLERS, path: ROUTE.CONTROLLERS, isStaticPage: true },
            { name: 'controllers', singularEntityName: 'controller', fieldName: 'name', path: ROUTE.EDIT_CONTROLLER },
            { pageName: PAGE_TITLE.CALIBRATION_CERTIFICATES, path: ROUTE.CALIBRATION_CERTIFICATES, isStaticPage: true },
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.CALIBRATION_CERTIFICATES}>
          <PageTitle title={PAGE_TITLE.CALIBRATION_CERTIFICATES} />
          <CalibrationCertificates />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.CALIBRATION_CERTIFICATE_CREATE,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { pageName: PAGE_TITLE.CONTROLLERS, path: ROUTE.CONTROLLERS, isStaticPage: true },
            { name: 'controllers', singularEntityName: 'controller', fieldName: 'name', path: ROUTE.EDIT_CONTROLLER },
            { pageName: PAGE_TITLE.CALIBRATION_CERTIFICATES, path: ROUTE.CALIBRATION_CERTIFICATES, isStaticPage: true },
            { pageName: PAGE_TITLE.CALIBRATION_CERTIFICATE_CREATE, path: ROUTE.CALIBRATION_CERTIFICATE_CREATE, isStaticPage: true },
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.CALIBRATION_CERTIFICATE_CREATE}>
          <PageTitle title={PAGE_TITLE.CALIBRATION_CERTIFICATE_CREATE} />
          <CalibrationCertificateForm />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.CALIBRATION_CERTIFICATE_EDIT,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { pageName: PAGE_TITLE.CONTROLLERS, path: ROUTE.CONTROLLERS, isStaticPage: true },
            { name: 'controllers', singularEntityName: 'controller', fieldName: 'name', path: ROUTE.EDIT_CONTROLLER },
            { pageName: PAGE_TITLE.CALIBRATION_CERTIFICATES, path: ROUTE.CALIBRATION_CERTIFICATES, isStaticPage: true },
            {
              name: 'calibration_certificates',
              singularEntityName: 'calibrationCertificate',
              fieldName: 'breadcrumb',
              path: ROUTE.CALIBRATION_CERTIFICATE_EDIT
            },
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.CALIBRATION_CERTIFICATE_EDIT}>
          <PageTitle title={PAGE_TITLE.CALIBRATION_CERTIFICATE_EDIT} />
          <CalibrationCertificateForm />
        </PermissionRoute>
      ),
    },
  ],
};

const pagesEditRecipe: RouteObject = {
  handle: {
    crumb: (): Crumb => ({ isSkip: true }),
  },
  element: (
    <EditOutlet />
  ),
  children: [
    {
      path: ROUTE.EDIT_RECIPE,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { pageName: PAGE_TITLE.RECIPES, path: ROUTE.RECIPES, isStaticPage: true },
            { name: 'recipes', singularEntityName: 'recipe', fieldName: 'name', path: ROUTE.EDIT_RECIPE }
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.EDIT_RECIPE}>
          <PageTitle title={PAGE_TITLE.EDIT_RECIPE} />
          <RecipeEdit />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.RECIPE_STEPS,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { pageName: PAGE_TITLE.RECIPES, path: ROUTE.RECIPES, isStaticPage: true },
            { name: 'recipes', singularEntityName: 'recipe', fieldName: 'name', path: ROUTE.EDIT_RECIPE },
            { pageName: PAGE_TITLE.RECIPE_STEPS, path: ROUTE.RECIPE_STEPS, isStaticPage: true }
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.RECIPE_STEPS}>
          <PageTitle title={PAGE_TITLE.RECIPE_STEPS} />
          <RecipeSteps />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.CREATE_RECIPE_STEPS,
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.CREATE_RECIPE_STEPS}>
          <PageTitle title={PAGE_TITLE.CREATE_RECIPE_STEPS} />
          <RecipeStepCreate />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.EDIT_RECIPE_STEPS,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { pageName: PAGE_TITLE.RECIPES, path: ROUTE.RECIPES, isStaticPage: true },
            { name: 'recipes', singularEntityName: 'recipe', fieldName: 'name', path: ROUTE.EDIT_RECIPE },
            { pageName: PAGE_TITLE.RECIPE_STEPS, path: ROUTE.RECIPE_STEPS, isStaticPage: true },
            { pageName: PAGE_TITLE.EDIT_RECIPE_STEPS, path: ROUTE.EDIT_RECIPE_STEPS, isStaticPage: true },
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.EDIT_RECIPE_STEPS}>
          <PageTitle title={PAGE_TITLE.EDIT_RECIPE_STEPS} />
          <RecipeStepEdit />
        </PermissionRoute>
      ),
    },
  ]
};

const pagesEditPreHeatRecipe: RouteObject = {
  handle: {
    crumb: (): Crumb => ({ isSkip: true }),
  },
  children: [
    {
      path: ROUTE.EDIT_PRE_HEAT_RECIPE,
      handle: {
        crumb: (): Crumb => ({ text: PAGE_TITLE.EDIT_PRE_HEAT_RECIPE }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.EDIT_PRE_HEAT_RECIPE}>
          <PageTitle title={PAGE_TITLE.EDIT_PRE_HEAT_RECIPE} />
          <PreHeatRecipeEdit />
        </PermissionRoute>
      ),
    },
  ]
};

const pages: RouteObject = {
  element: (<DefaultLayout />),
  handle: {
    crumb: (): Crumb => ({ text: HOME_PAGE_NAME }),
  },
  children: [
    {
      path: ROUTE.HOME,
      handle: {
        crumb: (): Crumb => ({ text: EMPTY_CRUMB_TEXT, isSkip: true }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={HOME_PAGE_NAME}>
          <HomePageChecker>
            <PageTitle title={HOME_PAGE_NAME} />
            <LocationsView />
          </HomePageChecker>
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.PROFILE,
      handle: {
        crumb: (): Crumb => ({ text: PAGE_TITLE.PROFILE }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.PROFILE}>
          <PageTitle title={PAGE_TITLE.PROFILE} />
          <Profile />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.ORGANIZATIONS,
      handle: {
        crumb: (): Crumb => ({ text: PAGE_TITLE.ORGANIZATIONS }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.ORGANIZATIONS}>
          <PageTitle title={PAGE_TITLE.ORGANIZATIONS} />
          <OrganizationsView />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.MEMBERS,
      handle: {
        crumb: (): Crumb => ({ text: PAGE_TITLE.MEMBERS }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.MEMBERS}>
          <PageTitle title={PAGE_TITLE.MEMBERS} />
          <MembersView title={PAGE_TITLE.MEMBERS} />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.CONTROLLERS,
      handle: {
        crumb: (): Crumb => ({ text: PAGE_TITLE.CONTROLLERS }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.CONTROLLERS}>
          <PageTitle title={PAGE_TITLE.CONTROLLERS} />
          <ControllersView />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.RECIPES,
      handle: {
        crumb: (): Crumb => ({ text: PAGE_TITLE.RECIPES }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.RECIPES}>
          <PageTitle title={PAGE_TITLE.RECIPES} />
          <RecipesView />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.LOCATIONS,
      handle: {
        crumb: (): Crumb => ({ text: PAGE_TITLE.LOCATIONS }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.LOCATIONS}>
          <PageTitle title={PAGE_TITLE.LOCATIONS} />
          <LocationsView />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.CONTRACTS,
      handle: {
        crumb: (): Crumb => ({ text: PAGE_TITLE.CONTRACTS }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.CONTRACTS}>
          <PageTitle title={PAGE_TITLE.CONTRACTS} />
          <ContractsView />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.LOCATION_CONTRACTS,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { name: 'locations', singularEntityName: 'location', fieldName: 'name', path: ROUTE.LOCATION_CONTRACTS }
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.LOCATION_CONTRACTS}>
          <LocationContractsView />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.LOCATION_CONTRACTS_OVERVIEW,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { name: 'locations', singularEntityName: 'location', fieldName: 'name', path: ROUTE.LOCATION_CONTRACTS },
            { pageName: PAGE_TITLE.OVERVIEW, path: ROUTE.LOCATION_CONTRACTS_OVERVIEW, isStaticPage: true }
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.OVERVIEW}>
          <LocationContractOverview />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.LOCATION_CONTRACT_SCREENS,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { name: 'locations', singularEntityName: 'location', fieldName: 'name', path: ROUTE.LOCATION_CONTRACTS },
            { name: 'contracts', singularEntityName: 'contract', fieldName: 'name', path: ROUTE.LOCATION_CONTRACT_SCREENS }
          ]
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.LOCATION_CONTRACT_SCREENS}>
          <ContractScreensView />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.SCREEN_CYCLES,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { name: 'locations', singularEntityName: 'location', fieldName: 'name', path: ROUTE.LOCATION_CONTRACTS },
            { name: 'contracts', singularEntityName: 'contract', fieldName: 'name', path: ROUTE.LOCATION_CONTRACT_SCREENS },
            { name: 'screens', singularEntityName: 'screen', fieldName: 'name', path: ROUTE.SCREEN_CYCLES }
          ]
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.SCREEN_CYCLES}>
          <ScreenCyclesView />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.CYCLE,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { name: 'locations', singularEntityName: 'location', fieldName: 'name', path: ROUTE.LOCATION_CONTRACTS },
            { name: 'contracts', singularEntityName: 'contract', fieldName: 'name', path: ROUTE.LOCATION_CONTRACT_SCREENS },
            { name: 'screens', singularEntityName: 'screen', fieldName: 'name', path: ROUTE.SCREEN_CYCLES },
            { name: 'cycles', singularEntityName: 'cycle', fieldName: 'name', path: ROUTE.CYCLE }
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.CYCLE}>
          <PageTitle title={PAGE_TITLE.CYCLE} />
          <CycleView />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.CYCLE_WITHOUT_SCREEN,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { name: 'locations', singularEntityName: 'location', fieldName: 'name', path: ROUTE.LOCATION_CONTRACTS },
            { name: 'contracts', singularEntityName: 'contract', fieldName: 'name', path: ROUTE.LOCATION_CONTRACT_SCREENS },
            { name: 'cycles', singularEntityName: 'cycle', fieldName: 'name', path: ROUTE.CYCLE_WITHOUT_SCREEN }
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.CYCLE}>
          <PageTitle title={PAGE_TITLE.CYCLE} />
          <CycleView />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.ADD_SCREEN_CYCLE,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { name: 'locations', singularEntityName: 'location', fieldName: 'name', path: ROUTE.LOCATION_CONTRACTS },
            { name: 'contracts', singularEntityName: 'contract', fieldName: 'name', path: ROUTE.LOCATION_CONTRACT_SCREENS },
            { name: 'screens', singularEntityName: 'screen', fieldName: 'name', path: ROUTE.SCREEN_CYCLES },
            { pageName: PAGE_TITLE.ADD_CYCLE, path: ROUTE.ADD_SCREEN_CYCLE, isStaticPage: true },
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.ADD_CYCLE}>
          <PageTitle title={PAGE_TITLE.ADD_CYCLE} />
          <AddCycle />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.ADD_CYCLE,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { name: 'locations', singularEntityName: 'location', fieldName: 'name', path: ROUTE.LOCATION_CONTRACTS },
            { name: 'contracts', singularEntityName: 'contract', fieldName: 'name', path: ROUTE.LOCATION_CONTRACT_SCREENS },
            { pageName: PAGE_TITLE.ADD_CYCLE, path: ROUTE.ADD_CYCLE, isStaticPage: true },
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.ADD_CYCLE}>
          <PageTitle title={PAGE_TITLE.ADD_CYCLE} />
          <AddCycle />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.ADD_CYCLE_BY_LOCATION,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { name: 'locations', singularEntityName: 'location', fieldName: 'name', path: ROUTE.LOCATION_CONTRACTS },
            { pageName: PAGE_TITLE.OVERVIEW, path: ROUTE.LOCATION_CONTRACTS_OVERVIEW, isStaticPage: true },
            { pageName: PAGE_TITLE.ADD_CYCLE, path: ROUTE.ADD_CYCLE_BY_LOCATION, isStaticPage: true },
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.ADD_CYCLE}>
          <PageTitle title={PAGE_TITLE.ADD_CYCLE} />
          <AddCycle />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.EDIT_CYCLE,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { name: 'locations', singularEntityName: 'location', fieldName: 'name', path: ROUTE.LOCATION_CONTRACTS },
            { name: 'contracts', singularEntityName: 'contract', fieldName: 'name', path: ROUTE.LOCATION_CONTRACT_SCREENS },
            { name: 'screens', singularEntityName: 'screen', fieldName: 'name', path: ROUTE.SCREEN_CYCLES },
            { name: 'cycles', singularEntityName: 'cycle', fieldName: 'name', path: ROUTE.EDIT_CYCLE }
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.EDIT_CYCLE}>
          <PageTitle title={PAGE_TITLE.EDIT_CYCLE} />
          <EditCycle />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.EDIT_CYCLE_WITHOUT_SCREEN,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { name: 'locations', singularEntityName: 'location', fieldName: 'name', path: ROUTE.LOCATION_CONTRACTS },
            { name: 'contracts', singularEntityName: 'contract', fieldName: 'name', path: ROUTE.LOCATION_CONTRACT_SCREENS },
            { name: 'cycles', singularEntityName: 'cycle', fieldName: 'name', path: ROUTE.EDIT_CYCLE_WITHOUT_SCREEN }
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.EDIT_CYCLE}>
          <PageTitle title={PAGE_TITLE.EDIT_CYCLE} />
          <EditCycle />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.CYCLE_REPORT,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { name: 'locations', singularEntityName: 'location', fieldName: 'name', path: ROUTE.LOCATION_CONTRACTS },
            { name: 'contracts', singularEntityName: 'contract', fieldName: 'name', path: ROUTE.LOCATION_CONTRACT_SCREENS },
            { name: 'screens', singularEntityName: 'screen', fieldName: 'name', path: ROUTE.SCREEN_CYCLES },
            { name: 'cycles', singularEntityName: 'cycle', fieldName: 'name', path: ROUTE.CYCLE },
            { pageName: PAGE_TITLE.CYCLE_REPORT, path: ROUTE.CYCLE_REPORT, isStaticPage: true }
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.CYCLE_REPORT}>
          <PageTitle title={PAGE_TITLE.CYCLE_REPORT} />
          <CycleReport />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.CYCLE_REPORTS,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { name: 'locations', singularEntityName: 'location', fieldName: 'name', path: ROUTE.LOCATION_CONTRACTS },
            { name: 'contracts', singularEntityName: 'contract', fieldName: 'name', path: ROUTE.LOCATION_CONTRACT_SCREENS },
            { name: 'screens', singularEntityName: 'screen', fieldName: 'name', path: ROUTE.SCREEN_CYCLES },
            { name: 'cycles', singularEntityName: 'cycle', fieldName: 'name', path: ROUTE.CYCLE },
            { pageName: PAGE_TITLE.CYCLE_REPORT, path: ROUTE.CYCLE_REPORT, isStaticPage: true }
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.CYCLE_REPORT}>
          <PageTitle title={PAGE_TITLE.CYCLE_REPORT} />
          <CycleReport />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.CYCLE_REPORT_WITHOUT_SCREEN,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { name: 'locations', singularEntityName: 'location', fieldName: 'name', path: ROUTE.LOCATION_CONTRACTS },
            { name: 'contracts', singularEntityName: 'contract', fieldName: 'name', path: ROUTE.LOCATION_CONTRACT_SCREENS },
            { name: 'cycles', singularEntityName: 'cycle', fieldName: 'name', path: ROUTE.CYCLE_WITHOUT_SCREEN },
            { pageName: PAGE_TITLE.CYCLE_REPORT, path: ROUTE.CYCLE_REPORT_WITHOUT_SCREEN, isStaticPage: true }
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.CYCLE_REPORT}>
          <PageTitle title={PAGE_TITLE.CYCLE_REPORT} />
          <CycleReport />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.CYCLE_REPORTS_WITHOUT_SCREEN,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { name: 'locations', singularEntityName: 'location', fieldName: 'name', path: ROUTE.LOCATION_CONTRACTS },
            { name: 'contracts', singularEntityName: 'contract', fieldName: 'name', path: ROUTE.LOCATION_CONTRACT_SCREENS },
            { name: 'cycles', singularEntityName: 'cycle', fieldName: 'name', path: ROUTE.CYCLE_WITHOUT_SCREEN },
            { pageName: PAGE_TITLE.CYCLE_REPORT, path: ROUTE.CYCLE_REPORTS_WITHOUT_SCREEN, isStaticPage: true }
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.CYCLE_REPORT}>
          <PageTitle title={PAGE_TITLE.CYCLE_REPORT} />
          <CycleReport />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.CREATE_MEMBERS,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { pageName: PAGE_TITLE.MEMBERS, path: ROUTE.MEMBERS, isStaticPage: true },
            { pageName: PAGE_TITLE.CREATE_MEMBERS, path: ROUTE.CREATE_MEMBERS, isStaticPage: true }
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.CREATE_MEMBERS}>
          <PageTitle title={PAGE_TITLE.CREATE_MEMBERS} />
          <CreateMember />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.EDIT_MEMBER,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { pageName: PAGE_TITLE.MEMBERS, path: ROUTE.MEMBERS, isStaticPage: true },
            { name: 'members', singularEntityName: 'member', fieldName: 'email', path: ROUTE.EDIT_MEMBER }
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.EDIT_MEMBER}>
          <PageTitle title={PAGE_TITLE.EDIT_MEMBER} />
          <EditMember />
        </PermissionRoute>
      ),
    },
    pagesEditControl,
    {
      path: ROUTE.CREATE_LOCATION,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { pageName: PAGE_TITLE.LOCATIONS, path: ROUTE.LOCATIONS, isStaticPage: true },
            { pageName: PAGE_TITLE.CREATE_LOCATION, path: ROUTE.CREATE_LOCATION, isStaticPage: true }
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.CREATE_LOCATION}>
          <PageTitle title={PAGE_TITLE.CREATE_LOCATION} />
          <CreateLocation />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.EDIT_LOCATION,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { pageName: PAGE_TITLE.LOCATIONS, path: ROUTE.LOCATIONS, isStaticPage: true },
            { name: 'locations', singularEntityName: 'location', fieldName: 'name', path: ROUTE.EDIT_LOCATION }
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.EDIT_LOCATION}>
          <PageTitle title={PAGE_TITLE.EDIT_LOCATION} />
          <EditLocation />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.CREATE_CONTRACT,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { pageName: PAGE_TITLE.CONTRACTS, path: ROUTE.CONTRACTS, isStaticPage: true },
            { pageName: PAGE_TITLE.CREATE_CONTRACT, path: ROUTE.CREATE_CONTRACT, isStaticPage: true }
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.CREATE_CONTRACT}>
          <PageTitle title={PAGE_TITLE.CREATE_CONTRACT} />
          <CreateContract />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.EDIT_CONTRACT,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { pageName: PAGE_TITLE.CONTRACTS, path: ROUTE.CONTRACTS, isStaticPage: true },
            { name: 'contracts', singularEntityName: 'contract', fieldName: 'name', path: ROUTE.EDIT_CONTRACT }
          ],
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.EDIT_CONTRACT}>
          <PageTitle title={PAGE_TITLE.EDIT_CONTRACT} />
          <EditContract />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.FORBIDDEN_RESOURCE,
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.FORBIDDEN_RESOURCE}>
          <PageTitle title={PAGE_TITLE.FORBIDDEN_RESOURCE} />
          <ForbiddenResourceView />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.CREATE_SCREEN,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { name: 'contracts', singularEntityName: 'contract', fieldName: 'name', path: ROUTE.LOCATION_CONTRACT_SCREENS },
            { pageName: PAGE_TITLE.CREATE_SCREEN, path: ROUTE.CREATE_SCREEN, isStaticPage: true }
          ]
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.CREATE_SCREEN}>
          <PageTitle title={PAGE_TITLE.CREATE_SCREEN} />
          <CreateScreen />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.CREATE_SCREEN_BY_LOCATION,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { name: 'locations', singularEntityName: 'location', fieldName: 'name', path: ROUTE.LOCATION_CONTRACTS },
            { pageName: PAGE_TITLE.OVERVIEW, path: ROUTE.LOCATION_CONTRACTS_OVERVIEW, isStaticPage: true },
            { pageName: PAGE_TITLE.CREATE_SCREEN, path: ROUTE.CREATE_SCREEN_BY_LOCATION, isStaticPage: true }
          ]
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.CREATE_SCREEN}>
          <PageTitle title={PAGE_TITLE.CREATE_SCREEN} />
          <CreateScreen />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.EDIT_SCREEN,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { name: 'contracts', singularEntityName: 'contract', fieldName: 'name', path: ROUTE.LOCATION_CONTRACT_SCREENS },
            { pageName: PAGE_TITLE.EDIT_SCREEN, path: ROUTE.EDIT_SCREEN, name: 'screens', singularEntityName: 'screen', fieldName: 'name', }
          ]
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.EDIT_SCREEN}>
          <PageTitle title={PAGE_TITLE.EDIT_SCREEN} />
          <EditScreen />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.EDIT_SCREEN_BY_OVERVIEW_PAGE,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { name: 'locations', singularEntityName: 'location', fieldName: 'name', path: ROUTE.LOCATION_CONTRACTS },
            { pageName: PAGE_TITLE.OVERVIEW, path: ROUTE.LOCATION_CONTRACTS_OVERVIEW, isStaticPage: true },
            { pageName: PAGE_TITLE.EDIT_SCREEN, path: ROUTE.EDIT_SCREEN_BY_OVERVIEW_PAGE, isStaticPage: true }
          ]
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.CREATE_SCREEN}>
          <PageTitle title={PAGE_TITLE.CREATE_SCREEN} />
          <EditScreen />
        </PermissionRoute>
      ),
    },
    {
      path: ROUTE.CREATE_RECIPE,
      handle: {
        crumb: (): Crumb => ({
          segments: [
            { pageName: PAGE_TITLE.RECIPES, path: ROUTE.RECIPES, isStaticPage: true },
            { pageName: PAGE_TITLE.CREATE_RECIPE, path: ROUTE.CREATE_RECIPE, isStaticPage: true }
          ]
        }),
      },
      element: (
        <PermissionRoute action={Action.Read} page={PAGE_TITLE.CREATE_RECIPE}>
          <PageTitle title={PAGE_TITLE.CREATE_RECIPE} />
          <RecipeCreate />
        </PermissionRoute>
      ),
    },
    pagesEditRecipe,
    pagesEditPreHeatRecipe,
  ]
};

const pageWithoutLayout: RouteObject[] = [
  {
    path: ROUTE.AUTH_FORGOT_PASSWORD,
    element: (
      <>
        <PageTitle title={PAGE_TITLE.AUTH_FORGOT_PASSWORD} />
        <ForgotPassword />
      </>
    ),
  },
  {
    path: ROUTE.AUTH_CONFIRM_PASSWORD_RESET,
    element: (
      <>
        <PageTitle title={PAGE_TITLE.AUTH_CONFIRM_PASSWORD_RESET} />
        <ResetPassword />
      </>
    ),
  },
  {
    path: ROUTE.AUTH_SIGN_IN,
    element: (
      <>
        <PageTitle title={PAGE_TITLE.AUTH_SIGN_IN} />
        <SignIn />
      </>
    ),
  },
  {
    path: ROUTE.NOT_FOUND,
    element: (
      <>
        <PageTitle title={PAGE_TITLE.NOT_FOUND} />
        <NotFoundPage />
      </>
    ),
  },
  {
    path: ROUTE.FORBIDDEN_RESOURCE,
    element: <ForbiddenResourceView />,
  },
];

const router = createBrowserRouter(
  [
    pages,
    pagesEditControl,
    ...pageWithoutLayout,
  ],
  {
    future: {},
  }
);

export default router;
